<script setup lang="ts">
  import { computed, ref } from 'vue';
  import { useResizeObserver } from '@vueuse/core';
  import { useCatalogPage } from '@/composition/catalog/catalog-page-context';
  import { catalogSectionId } from '@/composition/catalog/use-catalog-page-setup';
  import {
    isAvailableHere,
    TScheduleStatus,
  } from '@/domain/catalog/availability';
  import {
    EMPTY_SLICE,
    sliceByCategory,
    TSlices,
  } from '@/domain/catalog/catalog-slices';
  import V2CatalogCardPlaceholder from './V2CatalogCardPlaceholder.vue';
  import V2CatalogCategoryCard from './V2CatalogCategoryCard.vue';
  import V2CatalogDiscountsCard from './V2CatalogDiscountsCard.vue';
  import V2CatalogPopularCard from './V2CatalogPopularCard.vue';

  /**
   * The Menú page's list (plan §5.7, §5.8): one stable `<section>` per shown
   * category (the scroll-spy observes it; it holds the card or its
   * placeholder), «+ Nueva categoría» at the end, and the single hidden file
   * input every row's photo tile uses. Capped at 1120 px and centred.
   *
   * Page-wide per-product state is cut per category here: a card only
   * re-renders when its own part changes.
   */
  const {
    state,
    layout,
    data,
    access,
    url,
    windowing,
    schedule,
    actions,
  } = useCatalogPage();

  const root = ref<HTMLElement | null>(null);
  const photoInput = ref<HTMLInputElement | null>(null);
  /** The row whose photo tile opened the picker. */
  let photoItemId: number | null = null;

  // The row mode follows the list's own width (one observer, never per row).
  useResizeObserver(root, (entries) => {
    const width = entries[0]?.contentRect.width;
    if (width) layout.setListWidth(width);
  });

  //#region Per-card slices

  function categoryOf(itemId: number): number | null {
    return data.itemById.value[itemId]?.categoryId ?? null;
  }

  let selectedSlices: TSlices<true> = {};
  let highlightedSlices: TSlices<true> = {};
  let busySlices: TSlices<true> = {};
  let statusSlices: TSlices<TScheduleStatus | null> = {};

  const selected = computed(() => {
    selectedSlices = sliceByCategory(
      state.selectedIds,
      categoryOf,
      selectedSlices
    );
    return selectedSlices;
  });
  const highlighted = computed(() => {
    highlightedSlices = sliceByCategory(
      state.highlightIds,
      categoryOf,
      highlightedSlices
    );
    return highlightedSlices;
  });
  const photoBusy = computed(() => {
    busySlices = sliceByCategory(
      actions.photoBusyIds.value,
      categoryOf,
      busySlices
    );
    return busySlices;
  });
  const statuses = computed(() => {
    statusSlices = sliceByCategory(
      schedule.itemStatuses.value,
      categoryOf,
      statusSlices
    );
    return statusSlices;
  });

  /** The editor's product, and the category that owns it. */
  const editingItem = computed(() => {
    const editor = state.editor;
    if (editor?.kind !== 'item' || typeof editor.id !== 'number') return null;
    const categoryId = categoryOf(editor.id);
    return categoryId === null ? null : { id: editor.id, categoryId };
  });
  const editingCategoryId = computed(() =>
    state.editor?.kind === 'category' && typeof state.editor.id === 'number'
      ? state.editor.id
      : null
  );

  /** Products of each category that cannot be ordered here. */
  const unavailable = computed(() => {
    const branchId = data.branchId.value;
    const counts: Record<number, number> = {};
    for (const section of data.sections.value)
      counts[section.category.id] = section.items.filter(
        (item) => !isAvailableHere(item, branchId)
      ).length;
    return counts;
  });

  //#endregion

  function pickPhoto(itemId: number) {
    photoItemId = itemId;
    photoInput.value?.click();
  }

  function onPhotoPicked(event: Event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;
    const file = input.files?.[0] ?? null;
    // The same file can be picked again later.
    input.value = '';
    const item = photoItemId !== null ? data.itemById.value[photoItemId] : null;
    photoItemId = null;
    if (file && item) void actions.uploadPhoto(item, file);
  }

  function newCategory() {
    url.openCategory('new', layout.columns.value.asideDocked);
  }
</script>

<template>
  <div ref="root" class="v2-catalog-list">
    <V2CatalogDiscountsCard
      v-if="state.mode === 'browse' && !data.isFiltering.value"
    />
    <V2CatalogPopularCard
      v-if="state.mode === 'browse' && !data.isFiltering.value"
    />
    <section
      v-for="section in data.shownSections.value"
      :id="catalogSectionId(section.category.id)"
      :key="section.category.id"
      class="list-section"
      :data-category-id="section.category.id"
    >
      <V2CatalogCategoryCard
        v-if="windowing.isMounted(section.category.id)"
        :section="section"
        :unavailable-count="unavailable[section.category.id] || 0"
        :selected="selected[section.category.id] || EMPTY_SLICE"
        :highlighted="highlighted[section.category.id] || EMPTY_SLICE"
        :photo-busy="photoBusy[section.category.id] || EMPTY_SLICE"
        :statuses="statuses[section.category.id] || EMPTY_SLICE"
        :category-status="
          schedule.categoryStatuses.value[section.category.id] || null
        "
        :editing-item-id="
          editingItem && editingItem.categoryId === section.category.id
            ? editingItem.id
            : null
        "
        :editing-category="editingCategoryId === section.category.id"
        @photo="pickPhoto"
      />
      <V2CatalogCardPlaceholder
        v-else
        :category-id="section.category.id"
        :height="windowing.estimate(section)"
      />
    </section>

    <button
      v-if="
        access.canManage && state.mode === 'browse' && !data.isFiltering.value
      "
      type="button"
      class="list-new-category"
      @click="newCategory"
    >
      <i class="mdi mdi-plus new-icon" />
      {{ $t('menuPage.list.newCategory') }}
    </button>

    <input
      ref="photoInput"
      type="file"
      accept="image/*"
      class="list-photo-input"
      tabindex="-1"
      aria-hidden="true"
      @change="onPhotoPicked"
    />
  </div>
</template>

<style scoped lang="scss">
  .v2-catalog-list {
    @apply tw-mx-auto tw-w-full;
    max-width: 1120px;

    .list-section + .list-section {
      @apply tw-mt-3;
    }

    .list-new-category {
      @apply tw-mt-3 tw-flex tw-min-h-[52px] tw-w-full tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-dashed tw-border-neutral-300 tw-bg-transparent tw-text-sm tw-font-medium tw-text-neutral-600 tw-transition-colors tw-duration-150;

      &:hover {
        @apply tw-border-neutral-400 tw-bg-surface tw-text-neutral-900;
      }

      &:focus-visible {
        @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }

      .new-icon {
        @apply tw-mr-1.5 tw-text-lg tw-leading-none;
      }
    }

    .list-photo-input {
      @apply tw-hidden;
    }
  }
</style>
